<template>
    <Modal
        :title="
            props.playbook?.is_template
                ? 'Download Template'
                : 'Download Playbook'
        "
    >
        <slot></slot>
        <template #body="{ close }">
            <div style="display: none">
                <span ref="closeRef" @click="close"></span>
            </div>
            <div class="flex flex-col gap-2" v-if="!state.isDownloading">
                <div class="flex gap-2">
                    <input
                        type="radio"
                        name="mode"
                        value="images"
                        id="dl-images-only"
                        checked
                        v-model="state.mode"
                    />
                    <label for="dl-images-only">Images only</label>
                </div>
                <div class="flex gap-2">
                    <input
                        type="radio"
                        name="mode"
                        value="full"
                        id="dl-full"
                        v-model="state.mode"
                    />
                    <label for="dl-full">Images with notes</label>
                </div>
                <button
                    @click="download()"
                    class="
                        bg-primary
                        text-white
                        rounded-lg
                        mt-2
                        py-[6px]
                        w-fit
                        px-4
                    "
                >
                    Download
                </button>
            </div>
            <div class="flex flex-col gap-2 justify-center items-center" v-else>
                <LoadingCircle></LoadingCircle>
                <div class="mt-3 text-[16px] text-bold">
                    {{ state.downloadProgress }}
                </div>
            </div>

            <div class="hidden">
                <whiteboard :sport="props.sportType" is-play="true">
                </whiteboard>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import Modal from "./Modal";
import { reactive, onMounted, defineProps, ref } from "vue";
import emitter from "tiny-emitter/instance";
import jszip from "jszip";
import htmlToPdfmake from "html-to-pdfmake";
import pdfMake from "pdfmake/build/pdfmake";

const props = defineProps({
    playbook: {
        type: Object,
    },
    sportType: {
        type: String,
    },
});

const state = reactive({
    mode: "images",
});

const closeRef = ref(null);

function download() {
    switch (state.mode) {
        case "images":
            downloadImagesOnly();
            break;
        case "full":
            downloadFull();
            break;
    }
}

async function downloadImagesOnly() {
    state.isDownloading = true;
    const zip = new jszip();
    let pageIndex = 1;
    for (const play of props.playbook?.plays) {
        state.downloadProgress = `Downloading ${pageIndex} of ${props.playbook.plays.length}`;
        const resp = await axios.get("/plays/get-play-data", {
            params: {
                play_id: play.play_id,
            },
        });

        const wbImage = await getPlayImage(resp.data, "blob");
        if (!wbImage?.image) {
            continue;
        }
        zip.file(`${play.play_name}.png`, wbImage.image);
        pageIndex++;
    }

    zip.generateAsync({ type: "blob" }).then(function (blob) {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${props.playbook?.playbook_title}.zip`;
        link.click();
        link.remove();
        state.isDownloading = false;
        state.downloadProgress = "";
        closeModal();
    });
}
const teamIdFromUrl = new URLSearchParams(window.location.search).get("teamId");

async function loadImageAsset(url) {
    return new Promise((resolve, reject) => {
        let image = new Image();
        image.onload = function () {
            resolve({
                height: image.height,
                width: image.height,
            });
        };
        image.onerror = function () {
            console.log("error");
            reject();
        };
        image.src = url;
    });
}

const toBase64 = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
        img.src = url;
    });

async function downloadFull() {
    let teamName = "";
    let logo = "/default-images/team.png";
    let logoWidth = 40;
    let logoHeight = 40;

    if (teamIdFromUrl) {
        const _team = await axios.get(`/teams/getApi?teamId=${teamIdFromUrl}`);
        teamName = _team.data.name;
        if (_team.data.image) {
            logo = _team.data.image;

            const img = await loadImageAsset(logo);
            logoWidth = (logoHeight / img.width) * img.height;
        }
    }

    state.isDownloading = true;

    const logoBase64 = await toBase64(logo);

    const playbookTitle = props.playbook.playbook_title;

    const docContent = [
        {
            text: teamName,
            fontSize: 20,
            bold: true,
            alignment: "center",
            margin: [0, 50, 0, 20],
        },
        {
            image: logoBase64,
            width: 120,
            alignment: "center",
            margin: [0, 20, 0, 20],
        },
        {
            text: playbookTitle,
            bold: true,
            fontSize: 40,
            alignment: "center",
            margin: [0, 40, 0, 20],
        },
        { text: "", pageBreak: "after" },
    ];

    let pageIndex = 2;
    for (const play of props.playbook?.plays) {
        state.downloadProgress = `Downloading ${pageIndex - 1} of ${
            props.playbook.plays.length
        }`;
        const resp = await axios.get("/plays/get-play-data", {
            params: {
                play_id: play.play_id,
            },
        });

        const wbImage = await getPlayImage(resp.data, "dataURL");
        if (!wbImage?.image) {
            continue;
        }

        let playName = play.play_name;
        playName = playName.replace(" (Mirrored)", "");
        playName = playName.replace(" (Mirroring)", "");

        const playPage = [
            {
                text: playName,
                fontSize: 24,
                bold: true,
                alignment: "center",
                margin: [0, 10, 0, 20],
            },
            {
                image: wbImage.image, // already base64
                width: 545.28,
                alignment: "center",
                margin: [
                    0,
                    0,
                    0,
                    resp.data.play_note && resp.data.play_note != "null"
                        ? 20
                        : 0,
                ],
            },
        ];

        if (resp.data.play_note && resp.data.play_note != "null") {
            let playNote = resp.data.play_note;
            playNote = playNote.replaceAll("<hr><hr><hr>", "<hr>");
            playNote = playNote.replaceAll("<hr><hr>", "<hr>");
            const playNotePdfmake = htmlToPdfmake(playNote, {
                defaultStyles: {
                    p: {
                        fontSize: 9,
                        margin: [0, 0, 0, 0],
                    },
                },
            });

            playPage.push({
                stack: [playNotePdfmake],
                margin: [20, 0, 20, 0],
            });
        }

        playPage.push({ text: "", pageBreak: "after" });
        pageIndex++;

        docContent.push(...playPage);
    }

    const now = new Date().toLocaleString();
    const footerLogo = await toBase64("/images/logo.png");
    const docDefinition = {
        pageSize: "A4",
        pageMargins: [40, 60, 40, 60],
        content: docContent,
        footer: (currentPage, pageCount) => {
            if (currentPage !== 1) {
                return [];
            }
            return {
                columns: [
                    {
                        text: `Printed on ${now}`,
                        alignment: "left",
                        fontSize: 10,
                        margin: [40, 0, 0, 10],
                    },
                    {
                        image: footerLogo,
                        fit: [80, 20],
                        alignment: "right",
                        margin: [0, 0, 40, 10],
                    },
                ],
            };
        },
    };
    pdfMake
        .createPdf(docDefinition)
        .download(`${props.playbook.playbook_title}.pdf`);
    state.isDownloading = false;
    state.downloadProgress = "";
    closeModal();
}

function getPlayImage(play, mode) {
    return new Promise((resolve, reject) => {
        emitter.once("getBufferDataReplyFull", (e) => {
            resolve(e);
        });
        emitter.emit("wb-change-background", play.background);

        emitter.emit("wb-load-play", play);
        setTimeout(() => {
            emitter.emit("getBufferDataFull", mode);
        }, 3000);
    });
}

function closeModal() {
    closeRef.value?.click();
}
</script>
